/**
 * Order message service
 *
 * @version
 * 4.77.0 OAUI-722 Order messages: refactor to only use mongo messages (remove dependency on core-api)
 * 4.72.0 OAUI-703 Order messages POST not passing parameter for sending email notifications
 * 4.71.0 OAUI-693 Tidy and remove duplicated code
 * 4.71.0 OAUI-684 Improve shared code and add interfaces
 * 4.62.0 OAUI-654 Remove calls to getOrderCrudApiRouting
 * 4.58.0 OAUI-635 Add checks to Messages for MEAN order updating
 * 4.37.0 OAUI-527 Slowdowns in order modals
 * 4.36.0 OAUI-519 Add / Edit Adjustments
 * 4.36.0 OAUI-515 Add / Edit / Remove Order Messages
 * 4.36.0 OAUI-504 Refactor how order messages are fetched from API
 * 4.35.0 OAUI-499 View Order Messages
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @copyright (c) 2022-2024 Build Centrix
 */
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { WdLocalSharedFunctionsService } from '../../shared/wd-local-shared-functions.service';
import {
  IOrderMessageAPIModel,
  IOrderMessageModel,
  IOrderMessageModelRaw,
} from '../../../interfaces/orders/order-interfaces';
import { IBcxDateTimeHelper } from '../../../interfaces/generic/generic-interfaces';

@Injectable({
  providedIn: 'root',
})
export class WdOrderMessageService {
  // Messages
  messages = new BehaviorSubject<Array<any>>([]);

  // Behaviour Subjects
  isDeletingOrderMessages = new BehaviorSubject(false);
  isEditingOrderMessages = new BehaviorSubject(false);
  isUpdatingOrderMessages = new BehaviorSubject(false);

  // Booleans
  showTransactionMessages = true;
  showUserMessages = true;
  sendNotifications = true;

  /**
   * Constructor
   *
   * @since 4.35.0
   */
  constructor(
    private wdLocalSharedFunctions: WdLocalSharedFunctionsService,
    private wdTranslate: TranslateService,
    @Inject('wdDateTimeHelper') public wdDateTimeHelper: IBcxDateTimeHelper,
    @Inject('wdErrorService') public wdErrorService: any,
    @Inject('wdOauth2HttpService') public wdOauth2HttpService: any,
    @Inject('wdSharedFunctionsService') public wdSharedFunctionsService: any,
  ) {}

  /**
   * Add Order Message
   * @since 4.36.0
   */
  async addOrderMessage(orderIndex: string, message: string): Promise<void> {
    // Toggle Loader
    this.isUpdatingOrderMessages.next(true);
    try {
      // Format data for POST
      const data = this.buildMessageData(orderIndex, message, true);
      // Add order message
      await this.wdOauth2HttpService.post(
        `${this.wdLocalSharedFunctions.orderApiUrl}messages`,
        data,
      );
      // Success Message
      this.wdErrorService.userSuccess(
        this.wdTranslate.instant('labels.success'),
        this.wdTranslate.instant('outputs.successfullyAdded', {
          type: this.wdTranslate.instant('order.messages.singular.title'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
      throw error;
    } finally {
      // Toggle Loader
      this.isUpdatingOrderMessages.next(false);
    }
  }

  /**
   * Delete Order Message
   * @since 4.77.0
   */
  async deleteOrderMessage(
    messageId: string,
    orderIndex: string,
  ): Promise<void> {
    // Toggle Loader
    this.isDeletingOrderMessages.next(true);
    try {
      // Delete Order Message
      await this.wdOauth2HttpService.delete(
        `${this.wdLocalSharedFunctions.orderApiUrl}messages/${messageId}?orderIndex=${orderIndex}`,
      );
      // Success Message
      this.wdErrorService.userSuccess(
        this.wdTranslate.instant('labels.success'),
        this.wdTranslate.instant('outputs.successfullyDeleted', {
          type: this.wdTranslate.instant('order.messages.singular.title'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
      throw error;
    } finally {
      // Toggle Loader
      this.isDeletingOrderMessages.next(false);
    }
  }

  /**
   * Edit Order Message
   * @since 4.36.0
   */
  async editOrderMessage(
    orderIndex: string,
    messageDetails: any,
  ): Promise<void> {
    // Toggle Loader
    this.isEditingOrderMessages.next(true);
    try {
      const { id, message } = messageDetails;
      // Format data for PUT
      const data = this.buildMessageData(orderIndex, message);
      // Update Order Message
      await this.wdOauth2HttpService.put(
        `${this.wdLocalSharedFunctions.orderApiUrl}messages/${id}`,
        data,
      );
      // Success Message
      this.wdErrorService.userSuccess(
        this.wdTranslate.instant('labels.success'),
        this.wdTranslate.instant('outputs.successfullyUpdated', {
          type: this.wdTranslate.instant('order.messages.singular.title'),
        }),
      );
    } catch (error) {
      // Error Message
      this.wdErrorService.userError(
        this.wdTranslate.instant('labels.error'),
        error.statusText,
      );
      throw error;
    } finally {
      // Toggle Loader
      this.isEditingOrderMessages.next(false);
    }
  }

  /**
   * Build body data for order-api PUT/POST messages
   * @since 4.72.0
   */
  buildMessageData(orderIndex: string, message: string, isPost?: boolean) {
    const result: IOrderMessageAPIModel = {
      message: message,
      orderIndex: orderIndex,
    };
    if (isPost) {
      result.sendNotifications = this.sendNotifications;
    }
    return result;
  }

  /**
   * Get Message Time From Now
   * @since 4.35.0
   * @memberof WdOrderMessageService
   */
  getMessageDateTimeDetail(date: any): any {
    return this.wdDateTimeHelper.getTimeFromNow(date, false);
  }

  /**
   * Parse Order Message
   * @since 4.35.0
   * @memberof WdOrderMessageService
   */
  parseOrderMessage(message: IOrderMessageModelRaw): IOrderMessageModel {
    const type = message.user_message ? 'user' : 'transaction';
    const modified =
      message.date_modified === '0000-00-00 00:00:00'
        ? message.date_created
        : message.date_modified;
    const messageText = message.user_message
      ? message.user_message
      : message.transaction_message;
    const visibility = message.visibility;
    return {
      dateCreated: new Date(message.date_created),
      dateModified: new Date(modified),
      id: message.id,
      isEditing: false,
      isDeleting: false,
      type: type,
      user: message.user,
      message: messageText,
      dateTimeDisplay: this.getMessageDateTimeDetail(modified),
      visibility: visibility,
    };
  }

  /**
   * Parse Order Messages
   * @since 4.35.0
   * @memberof WdOrderMessageService
   */
  parseOrderMessages(messages: any): Array<any> {
    return messages.map((message: any) => this.parseOrderMessage(message));
  }
}
